// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { ascending } from "d3-array";
import { z } from "zod";

import { dateStringSchema } from "../../utils/zod";

const UsTnIncidentSchema = z.object({
  incidentClass: z.string(),
  incidentDate: dateStringSchema,
  incidentTypeCode: z.string(),
});

const UsTnIncidentPeriodReportSchema = z.object({
  numIncidents: z.number(),
  incidentTimePeriod: z.string(),
  incidents: z.array(UsTnIncidentSchema),
});
type UsTnIncidentPeriodReport = z.output<typeof UsTnIncidentPeriodReportSchema>;

const UsTnConviction = z.object({
  description: z.string(),
  imposedDate: dateStringSchema,
});

const UsTnProgramCompletionSchema = z.object({
  completionDate: dateStringSchema,
  programCode: z.string(),
  programType: z.string(),
});

type UsTnProgramCompletion = z.output<typeof UsTnProgramCompletionSchema>;

function formatConvictions(
  convictions: z.output<typeof UsTnConviction>[] | null,
): string {
  if (convictions === null) return "";

  return convictions
    .map((c) => `${c.description} on ${c.imposedDate.toLocaleDateString()}`)
    .join("/n");
}
export const q1Notes = z
  .object({
    listPriorNonTdocConvictions60Months: z
      .array(UsTnConviction)
      .nullable()
      .default([])
      .transform(formatConvictions),
    listPriorViolentTdocConvictions60Months: z
      .array(UsTnConviction)
      .nullable()
      .default([])
      .transform(formatConvictions),
  })
  .optional()
  .default({
    listPriorNonTdocConvictions60Months: [],
    listPriorViolentTdocConvictions60Months: [],
  });

export const q2Notes = z
  .array(z.string())
  .optional()
  .transform((notes) => {
    return notes?.join(", ") ?? "";
  });

const formatIncidentReportPeriod = (
  period: UsTnIncidentPeriodReport,
  padding = "",
) => {
  return period.incidents
    .map((i) => {
      return `${padding}${i.incidentTypeCode} (Class ${i.incidentClass}) on ${i.incidentDate.toLocaleDateString()}`;
    })
    .join(", ");
};

export function formatMultiplePeriodReports(
  periods: UsTnIncidentPeriodReport[],
): string {
  return periods
    .sort((a, b) =>
      ascending(
        // Pad the start of the period before sorting so 000-6 comes before 06-12 comes before 12-18.
        // The 12 comes from "XX-YY months".length -> 12
        a.incidentTimePeriod.padStart(12, "0"),
        b.incidentTimePeriod.padStart(12, "0"),
      ),
    )
    .map((p) => {
      const header = `Disciplinaries in last ${p.incidentTimePeriod}:\n`;
      return header + formatIncidentReportPeriod(p, "  ");
    })
    .join("\n");
}

const formatSinglePeriodReport = (
  notes: UsTnIncidentPeriodReport[] | undefined,
) => {
  if (!notes || notes.length === 0) return "";

  return formatIncidentReportPeriod(notes[0]);
};

export const singleIncidentPeriodReportSchema = z
  .array(UsTnIncidentPeriodReportSchema)
  .optional()
  .transform(formatSinglePeriodReport);

export const multiIncidentPeriodReportSchema = z
  .array(UsTnIncidentPeriodReportSchema)
  .optional()
  .default([]);

function formatProgramCompletions(reports: UsTnProgramCompletion[]): string {
  return reports
    .map(
      ({ programCode, programType, completionDate }) =>
        `Completed ${programCode} (${programType}) on ${completionDate.toLocaleDateString()}`,
    )
    .join("\n");
}

export const q7Notes = z
  .array(UsTnProgramCompletionSchema)
  .optional()
  .default([])
  .transform(formatProgramCompletions);

const booleanToString = z.boolean().transform((x) => x.toString());

export const coverSheetInformationSchema = z.object({
  levelOfCare: z.string().optional(),
  sentenceReleaseEligibilityDate: dateStringSchema.optional(),
  latestVantageCompletedDate: dateStringSchema.optional(),
  latestVantageRiskLevel: z.string().optional(),
  healthClassification: z.string().optional(),
  sentenceExpirationDate: dateStringSchema.optional(),
  activeRecommendations: z.array(
    z.object({
      Recommendation: z.string(),
      // // May also include these fields we're not using and therefore don't need to validate:
      // Pathway: z.string(),
      // PathwayName: z.string(),
      // TreatmentGoal: z.string(),
      // VantagePointTitle: z.string(),
    }),
  ),
  hasIncompatibles: z.boolean(),
  incompatibleArray: z.array(
    z.object({
      incompatibleOffenderId: z.string(),
      incompatibleType: z.string(),
    }),
  ),
  statusAtHearingSeg: z.string(),
  isServingLife: booleanToString,
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const partialCoverSheetInformation = coverSheetInformationSchema.partial();

export type CoverSheetFormData = z.infer<typeof partialCoverSheetInformation>;

export type UsTnCoverSheetSharedDraftData = {
  institutionName: string;
  residentFullName: string;
  omsId: string;
  date: string;
  statusAtHearing: string;
  hasIncompatibles: boolean;
  incompatiblesList: string;
  inmateWaivesNotice: boolean;
  currentCustodyLevel: string;
  recommendationFacilityAssignment: string;
  recommendationTransfer: boolean;
  recommendationCustodyLevel: string;
  recommendationOverrideType: string;
  recommendationJustification: string;
  updatedPhotoNeeded: boolean;
  emergencyContactUpdated: boolean;
  emergencyContactUpdatedDate: string;
  inmateAppeal: boolean;
  disagreementReasons: string;
  denialReasons: string;
  finalizingCounselor: string;
  finalApprovalDate: string;
  counselorRecommendedOverride: string;
  counselorRecommendedCustody: string;
  classificationType: string;

  // used for the headers at the top of the summary sheet
  // and only filled out for 2026 pilot opps
  trusteeNotConvictedOfFirstDegreeMurder: string;
  isServingLife: string;
  trusteeHas10YearsOrLessRemaining: string;
  trusteeNotServingForSexualOffense: string;
  checklistCompletedOnOverride: string;

  // used as part of the hearing notice form
  hearingDate: string;
  hearingLocation: string;
};

const eventNote = z.object({
  eventDate: dateStringSchema,
  noteBody: z.string(),
});

// ZodObject base. Exported so consumers that need ZodObject-only methods
// (`.omit()`, `.extend()`) can apply them before reapplying the rename transform.
export const formInformationBaseSchema = coverSheetInformationSchema.merge(
  z.object({
    currentOffenses: z.string().array().optional(),
    // These two fields come in from the platform under their new names
    // (`form_information_last_assessment_date` / `_total_score`) and get
    // remapped below to the in-app keys `lastCafDate` / `lastCafTotal` that
    // user form drafts in Firestore are already saved under.
    lastAssessmentDate: dateStringSchema.optional(),
    lastAssessmentTotalScore: z.string().optional(),
    latestClassificationDate: dateStringSchema.optional(),
    q1Score: z.coerce.number(),
    q2Score: z.coerce.number(),
    q3Score: z.coerce.number(),
    q4Score: z.coerce.number(),
    q5Score: z.coerce.number(),
    q6Score: z.coerce.number(),
    q7Score: z.coerce.number(),
    q8Score: z.coerce.number(),
    q9Score: z.coerce.number(),
    q6Notes: z.optional(z.array(eventNote)),
    q7Notes: z.optional(z.array(eventNote).or(eventNote.transform((n) => [n]))),
    q8Notes: z.optional(
      z.array(
        z.object({
          detainerReceivedDate: dateStringSchema,
          detainerFelonyFlag: z
            .string()
            .nullable()
            .transform((raw) => raw === "X"),
          detainerMisdemeanorFlag: z
            .string()
            .nullable()
            .transform((raw) => raw === "X"),
          jurisdiction: z.string().nullish(),
          description: z.string().nullish(),
          chargePending: z
            .string()
            .nullish()
            .transform((raw) => {
              if (raw === "Y") return true;
              if (raw === "N") return false;
              return undefined;
            }),
        }),
      ),
    ),
  }),
);

export const renameLastAssessmentToLastCaf = <
  T extends {
    lastAssessmentDate?: Date;
    lastAssessmentTotalScore?: string;
  },
>({
  lastAssessmentDate,
  lastAssessmentTotalScore,
  ...rest
}: T): Omit<T, "lastAssessmentDate" | "lastAssessmentTotalScore"> & {
  lastCafDate?: Date;
  lastCafTotal?: string;
} => ({
  ...rest,
  ...(lastAssessmentDate !== undefined && { lastCafDate: lastAssessmentDate }),
  ...(lastAssessmentTotalScore !== undefined && {
    lastCafTotal: lastAssessmentTotalScore,
  }),
});

export const formInformationSchema = formInformationBaseSchema
  .partial()
  .transform(renameLastAssessmentToLastCaf);

export const trusteeFormSchema = z.object({
  trusteeHas10YearsOrLessRemaining: booleanToString,
  trusteeNoAssaultiveDisciplinaryWithSeriousInjury: booleanToString,
  trusteeNoEscapeFromLowTrusteePast5Years: booleanToString,
  trusteeNoEscapeFromMediumCloseMaxPast10Years: booleanToString,
  trusteeNotConvictedOfFirstDegreeMurder: booleanToString,
  trusteeNotScoredHighForViolence: booleanToString,
  trusteeNotServingForSexualOffense: booleanToString,

  trusteeNotConvictedOfViolentOffenseOr12MonthsInCustody:
    booleanToString.nullish(),
  trusteeNoViolentFelonyConvictionPast5YearsIncarceration:
    booleanToString.nullish(),
  trusteeNotOnClinicalAlertStatus: booleanToString.nullish(),
  trusteeNotOnLevelOfCare3Or4Or5: booleanToString.nullish(),
  trusteeNoAssaultiveDisciplinaryWithSeriousInjuryLast5Years:
    booleanToString.nullish(),
  trusteeNoAssaultiveDisciplinaryWithSeriousInjuryMoreThan5YearsAgo:
    booleanToString.nullish(),
  trusteeHas7YearsOrLessRemaining: booleanToString.nullish(),
  trusteeNoDetainersOrWarrants: booleanToString.nullish(),
  trusteeNoPendingFelonyCharges: booleanToString.nullish(),
  trusteeNoPendingImmigrationActions: booleanToString.nullish(),
});

/** Criteria the rework autofills; stripped while `trusteeChecklistRework` is off. */
export const TRUSTEE_REWORK_PREFILL_KEYS = [
  "trusteeNotConvictedOfViolentOffenseOr12MonthsInCustody",
  "trusteeNoViolentFelonyConvictionPast5YearsIncarceration",
  "trusteeNotOnClinicalAlertStatus",
  "trusteeNotOnLevelOfCare3Or4Or5",
  "trusteeNoAssaultiveDisciplinaryWithSeriousInjuryLast5Years",
  "trusteeNoAssaultiveDisciplinaryWithSeriousInjuryMoreThan5YearsAgo",
  "trusteeHas7YearsOrLessRemaining",
  "trusteeNoDetainersOrWarrants",
  "trusteeNoPendingFelonyCharges",
  "trusteeNoPendingImmigrationActions",
] as const satisfies (keyof z.output<typeof trusteeFormSchema>)[];

export function stripTrusteeReworkPrefills<T extends object>(
  formInformation: T,
): T {
  const stripped = { ...formInformation };
  for (const key of TRUSTEE_REWORK_PREFILL_KEYS) {
    delete (stripped as Record<string, unknown>)[key];
  }
  return stripped;
}

/** Fields the counselor fills in by hand. Everything computable lives in `trusteeFormSchema`. */
export type TrusteeFormAdditionalFields = {
  trusteeNoFelonyDetainers: string;
  trusteeNotesForWarden: string;

  // Separate from the legacy trustee*Signature fields, so toggling the variant
  // cannot leave a form showing half-migrated approval state.
  trusteeWardenTrusteeApproved: string;
  trusteeWardenTrusteeApprovalDate: string;
  trusteeWardenAnnexApproved: string;
  trusteeWardenAnnexApprovalDate: string;
  trusteeContractMonitorApproved: string;
  trusteeContractMonitorApprovalDate: string;
  trusteeACApproved: string;
  trusteeACApprovalDate: string;
  trusteeWardenHasApproved: string;
  trusteeDenialReasons: string;
  /** Free text under the derived denial paragraph, which is not editable. */
  trusteeDenialNotes: string;
  trusteeCustodyApproved: string;
  trusteeWardenSignature: boolean;
  trusteeWardenSignatureDate: string;
  trusteeCMSignature: boolean;
  trusteeCMSignatureDate: string;
  trusteeACSignature: boolean;
  trusteeACSignatureDate: string;
};

export type TrusteeFormSchema = z.output<typeof trusteeFormSchema> &
  TrusteeFormAdditionalFields;

export const TRUSTEE_FORM_QUESTION_ORDER = [
  "trusteeHas10YearsOrLessRemaining",
  "trusteeNotConvictedOfViolentOffenseOr12MonthsInCustody",
  "trusteeNotServingForSexualOffense",
  "trusteeNoFelonyDetainers",
  "trusteeNoPendingFelonyCharges",
  "trusteeNoPendingImmigrationActions",
  "trusteeNoAssaultiveDisciplinaryWithSeriousInjury",
  "trusteeNoViolentFelonyConvictionPast5YearsIncarceration",
  "trusteeNoEscapeFromMediumCloseMaxPast10Years",
  "trusteeNoEscapeFromLowTrusteePast5Years",
  "trusteeNotScoredHighForViolence",
  "trusteeWardenHasApproved",
] satisfies (keyof TrusteeFormSchema)[];
